import { Icon, IconTypes } from '../../atoms';
import { KebapMenu } from '../../navigation';
import { KebapMenuItem } from '../../navigation';
import styles from './search-page-filter.module.scss';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useDetectClickOutside } from 'react-detect-click-outside';

export type SearchPageFilterOption<T> = {
  key: T;
  title?: React.ReactNode;
  icon?: IconTypes;
}

export type SearchPageFilterProps<T> = {
  translations: {
    label: string;
  };
  value: T;
  options: SearchPageFilterOption<T>[];
  onValueChange: (value: SearchPageFilterOption<T>) => void;
  defaultOptionKey: T;
}

export const SearchPageFilter = <T extends string | number,>({ translations, value, options, onValueChange, defaultOptionKey }: SearchPageFilterProps<T>) => {
  const isMounted = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<T>(value);

  const containerRef = useDetectClickOutside({
    onTriggered: (): void => {
      setIsOpen(false);
    },
  });

  const findOptionByKey = useCallback((key: T): SearchPageFilterOption<T> => {
    const option = options.find((option) => option.key === key);

    return option ?? options.find((option) => option.key === defaultOptionKey) ?? options[0];
  }, [defaultOptionKey, options]);

  useEffect(() => {
    if (value === selectedValue) {
      return;
    }

    isMounted.current = false; // block update
    setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    if (isMounted.current) {
      onValueChange(findOptionByKey(selectedValue));
    } else {
      isMounted.current = true;
    }
  }, [selectedValue]);

  return (
    <div className={styles.searchPageFilter} ref={containerRef}>
      <div className={styles.searchPageFilterInner} onClick={() => setIsOpen(!isOpen)}>
        <label className={styles.searchPageFilterLabel}>{translations.label}</label>
        <div className={styles.searchPageFilterValue}>
          <span>{findOptionByKey(selectedValue)?.title}</span>
          <Icon icon={'dropdown'} size={'20px'} />
        </div>
      </div>

      <KebapMenu<T>
        isOpen={isOpen}
      >
        {options.map((option) => (
          <KebapMenuItem<T>
            key={option.key}
            icon={option.icon}
            onClick={() => {
              console.log(`SearchPageFilter: onClick: ${option.key}`);
              setSelectedValue(option.key);
              setIsOpen(false);
            }}
            isActive={selectedValue === option.key}
          >
            {option.title}
          </KebapMenuItem>
        ))}
      </KebapMenu>
    </div>
  );
};
